"use client"

import * as React from "react"
import { useAuth } from "@clerk/clerk-react"
import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"

import {
  acceptInvitationById,
  acceptInvitation,
  createBranch,
  declineInvitationById,
  declineInvitation,
  getAuthMe,
  getInvitationPreview,
  getNewestUnviewedWorkspaceInvitation,
  getTenantBootstrap,
  markWorkspaceInvitationsViewed,
  setDefaultWorkspace,
  getWorkspaceBySlug,
  resolveInvitation,
  resolveTenantAccess,
} from "@/features/auth/services/auth-api"
import {
  type AcceptWorkspaceInvitationRequest,
  type AuthMeResponse,
  type DeclineWorkspaceInvitationRequest,
  type MarkWorkspaceInvitationsViewedRequest,
  type SetDefaultWorkspaceRequest,
  type TenantBootstrapResponse,
} from "@/features/auth/model/auth-types"
import { useAuthContextStore } from "@/features/auth/services/auth-context-store"
import {
  applyAuthoritativeAuthContext,
  applyResolvedTenantContext,
} from "@/features/auth/services/auth-context-sync"
import {
  AUTHORIZATION_SYNC_QUERY_OPTIONS,
} from "@/features/auth/services/authorization-sync-policy"

/** Prefix for `invalidateQueries` — matches every per-user `auth` /me cache entry. */
export const AUTH_ME_QUERY_KEY = ["auth", "me"] as const
export const AUTH_BOOTSTRAP_QUERY_KEY = ["auth", "bootstrap"] as const

export function buildAuthMeQueryKey(clerkUserId: string | null | undefined) {
  return [...AUTH_ME_QUERY_KEY, clerkUserId ?? "signed-out"] as const
}

export function buildAuthBootstrapQueryKey(
  clerkUserId: string | null | undefined,
  workspaceSlug: string | null | undefined,
  branchSlug: string | null | undefined,
) {
  return [
    ...AUTH_BOOTSTRAP_QUERY_KEY,
    clerkUserId ?? "signed-out",
    workspaceSlug ?? "",
    branchSlug ?? "",
  ] as const
}

const INVITE_RESOLVE_QUERY_KEY = ["auth", "invitation", "resolve"] as const
const INVITE_PREVIEW_QUERY_KEY = ["auth", "invitation", "preview"] as const
export const UNVIEWED_WORKSPACE_INVITATION_QUERY_KEY = ["auth", "invitation", "unviewed"] as const
export const AUTH_TENANT_ACCESS_RESOLVE_QUERY_KEY = ["auth", "tenant-access", "resolve"] as const
export const WORKSPACE_BY_SLUG_QUERY_KEY = ["workspace", "slug"] as const

/**
 * Cross-session permission changes must reach an already-open tenant app without a hard reload.
 * The backend still denies revoked actions immediately; this interval bounds how long passive UI chrome can
 * remain stale when the user makes no request and does not refocus the window.
 */
export function createAuthMeFromBootstrap(bootstrap: TenantBootstrapResponse): AuthMeResponse {
  return {
    auth: bootstrap.auth,
    user: bootstrap.user,
    authorizationRevision: bootstrap.authorizationRevision,
    state: bootstrap.state,
    workspaceAccess: bootstrap.workspaceAccess,
    pendingInvitations: [],
    expiredInvitations: [],
  }
}

/**
 * Fetches /tenant/auth/me, updates Zustand + React Query cache, and invalidates tenant-access resolve
 * queries used by admin route guards. Use after mutations that change the current user's
 * workspace role or module access.
 */
export async function refreshAuthMeContext(queryClient: QueryClient): Promise<AuthMeResponse> {
  const authMe = await getAuthMe()
  applyAuthoritativeAuthContext(authMe)
  queryClient.setQueryData(buildAuthMeQueryKey(authMe.auth.clerkUserId), authMe)
  await queryClient.invalidateQueries({ queryKey: AUTH_BOOTSTRAP_QUERY_KEY })
  await queryClient.invalidateQueries({ queryKey: AUTH_TENANT_ACCESS_RESOLVE_QUERY_KEY })
  return authMe
}

export function useTenantBootstrapQuery(
  workspaceSlug: string | undefined,
  branchSlug: string | undefined,
  options: { enabled?: boolean } = {},
) {
  const queryClient = useQueryClient()
  const { isLoaded, isSignedIn, userId } = useAuth()
  const setHandlingUnauthorized = useAuthContextStore((state) => state.setHandlingUnauthorized)
  const setAuthMeStatus = useAuthContextStore((state) => state.setAuthMeStatus)
  const setAuthMeFetchStatus = useAuthContextStore((state) => state.setAuthMeFetchStatus)
  const setAuthMeAttempted = useAuthContextStore((state) => state.setAuthMeAttempted)
  const enabled = Boolean(options.enabled !== false && isLoaded && isSignedIn)

  const query = useQuery({
    queryKey: buildAuthBootstrapQueryKey(userId, workspaceSlug, branchSlug),
    enabled,
    gcTime: 5 * 60 * 1000,
    retry: false,
    ...AUTHORIZATION_SYNC_QUERY_OPTIONS,
    // Permission edits commonly happen in another browser. Returning to this window must bypass staleTime and
    // ask the backend immediately, for grants as well as revocations.
    queryFn: async () => {
      const bootstrap = await getTenantBootstrap()
      const authMe = createAuthMeFromBootstrap(bootstrap)
      queryClient.setQueryData(buildAuthMeQueryKey(authMe.auth.clerkUserId), authMe)
      setHandlingUnauthorized(false)
      applyAuthoritativeAuthContext(authMe)
      if (bootstrap.currentContext.access.allowed) {
        applyResolvedTenantContext({
          workspaceId: bootstrap.currentContext.resolved.workspace?.id ?? undefined,
          workspaceSlug: bootstrap.currentContext.resolved.workspace?.slug ?? undefined,
        })
      }
      return bootstrap
    },
  })
  const hasResolvedData = Boolean(query.data)

  React.useEffect(() => {
    if (query.fetchStatus === "fetching") {
      // A background authorization refresh must not demote an already resolved session to pending. Consumers
      // may show freshness separately through fetchStatus while continuing to render the cached permissions.
      setAuthMeStatus(hasResolvedData ? "success" : "pending")
      setAuthMeFetchStatus("fetching")
      setAuthMeAttempted(true)
      return
    }

    if (query.status === "success") {
      setAuthMeStatus("success")
      setAuthMeFetchStatus("idle")
      setAuthMeAttempted(true)
      return
    }

    if (query.status === "error") {
      // React Query retains the last successful data after a failed refetch. Preserve that resolved state so a
      // momentary offline/5xx response cannot replace the whole app with an authentication loading screen.
      setAuthMeStatus(hasResolvedData ? "success" : "error")
      setAuthMeFetchStatus("idle")
      setAuthMeAttempted(true)
      return
    }

    setAuthMeStatus("idle")
    setAuthMeFetchStatus("idle")
    setAuthMeAttempted(false)
  }, [
    hasResolvedData,
    query.fetchStatus,
    query.status,
    setAuthMeAttempted,
    setAuthMeFetchStatus,
    setAuthMeStatus,
  ])

  return query
}

export function useTenantAccessResolveQuery(
  workspaceSlug: string | undefined,
  branchSlug: string | undefined,
  options: { enabled?: boolean } = {},
) {
  const enabled = Boolean(
    options.enabled !== false && workspaceSlug && branchSlug,
  )
  return useQuery({
    queryKey: [...AUTH_TENANT_ACCESS_RESOLVE_QUERY_KEY, workspaceSlug ?? "", branchSlug ?? ""] as const,
    enabled,
    queryFn: () =>
      resolveTenantAccess({
        workspaceSlug: workspaceSlug ?? "",
        branchSlug: branchSlug ?? "",
      }),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: false,
  })
}

export function useWorkspaceBySlugQuery(slug: string | null | undefined, options: { enabled?: boolean } = {}) {
  const enabled = Boolean(options.enabled !== false && slug)

  return useQuery({
    queryKey: [...WORKSPACE_BY_SLUG_QUERY_KEY, slug ?? ""] as const,
    enabled,
    queryFn: () => getWorkspaceBySlug(slug ?? ""),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: false,
  })
}

export function useAuthMeQuery(options: { enabled?: boolean } = {}) {
  const { isLoaded, isSignedIn, userId } = useAuth()
  const isHandlingUnauthorized = useAuthContextStore((state) => state.isHandlingUnauthorized)
  const shouldAutoFetch =
    options.enabled ?? (isLoaded && isSignedIn && !isHandlingUnauthorized)

  return useQuery({
    queryKey: buildAuthMeQueryKey(userId),
    enabled: shouldAutoFetch,
    placeholderData: undefined,
    staleTime: 30 * 1000,
    queryFn: () => getAuthMe(),
    retry: false,
  })
}

export function useResolveInvitationQuery(
  code: string | null,
  workspaceId?: string | null,
  options: { enabled?: boolean } = {},
) {
  const { getToken, isLoaded, isSignedIn } = useAuth()
  return useQuery({
    queryKey: [...INVITE_RESOLVE_QUERY_KEY, code, workspaceId ?? null],
    enabled: isLoaded && isSignedIn && Boolean(code) && options.enabled !== false,
    queryFn: async () => {
      const token = await getToken()
      if (!code) throw new Error("Missing invitation code.")
      return resolveInvitation(code, { token, workspaceId })
    },
    retry: false,
    refetchOnWindowFocus: false,
  })
}

export function useInvitationPreviewQuery(
  invitationId: string | null,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: [...INVITE_PREVIEW_QUERY_KEY, invitationId ?? "missing"] as const,
    enabled: Boolean(options.enabled !== false && invitationId),
    queryFn: async () => {
      if (!invitationId) throw new Error("Missing invitation id.")
      return getInvitationPreview(invitationId)
    },
    retry: false,
    refetchOnWindowFocus: false,
  })
}

export function useUnviewedWorkspaceInvitationQuery(options: { enabled?: boolean } = {}) {
  const { isLoaded, isSignedIn, userId } = useAuth()

  return useQuery({
    queryKey: [...UNVIEWED_WORKSPACE_INVITATION_QUERY_KEY, userId ?? "signed-out"] as const,
    enabled: Boolean(options.enabled !== false && isLoaded && isSignedIn),
    queryFn: getNewestUnviewedWorkspaceInvitation,
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: 45 * 1000,
  })
}

export function useMarkWorkspaceInvitationsViewedMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: MarkWorkspaceInvitationsViewedRequest) => markWorkspaceInvitationsViewed(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: UNVIEWED_WORKSPACE_INVITATION_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: AUTH_BOOTSTRAP_QUERY_KEY })
    },
  })
}

export function useAcceptInvitationMutation() {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: AcceptWorkspaceInvitationRequest) => {
      const token = await getToken()
      return acceptInvitation(input, token)
    },
    onSuccess: (response) => {
      if (response.authContext) {
        applyAuthoritativeAuthContext(response.authContext)
        queryClient.setQueryData(
          buildAuthMeQueryKey(response.authContext.auth.clerkUserId),
          response.authContext,
        )
      }
      queryClient.removeQueries({ queryKey: INVITE_RESOLVE_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: UNVIEWED_WORKSPACE_INVITATION_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: AUTH_BOOTSTRAP_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY })
    },
  })
}

export function useAcceptInvitationByIdMutation() {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (invitationId: string) => {
      const token = await getToken()
      return acceptInvitationById(invitationId, token)
    },
    onSuccess: (response) => {
      if (response.authContext) {
        applyAuthoritativeAuthContext(response.authContext)
        queryClient.setQueryData(
          buildAuthMeQueryKey(response.authContext.auth.clerkUserId),
          response.authContext,
        )
      }
      queryClient.removeQueries({ queryKey: INVITE_RESOLVE_QUERY_KEY })
      queryClient.removeQueries({ queryKey: INVITE_PREVIEW_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: UNVIEWED_WORKSPACE_INVITATION_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: AUTH_BOOTSTRAP_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY })
    },
  })
}

export function useDeclineInvitationMutation() {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: DeclineWorkspaceInvitationRequest) => {
      const token = await getToken()
      return declineInvitation(input, token)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AUTH_BOOTSTRAP_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: UNVIEWED_WORKSPACE_INVITATION_QUERY_KEY })
    },
  })
}

export function useDeclineInvitationByIdMutation() {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (invitationId: string) => {
      const token = await getToken()
      return declineInvitationById(invitationId, token)
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: INVITE_PREVIEW_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: AUTH_BOOTSTRAP_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: UNVIEWED_WORKSPACE_INVITATION_QUERY_KEY })
    },
  })
}

export function useSetDefaultWorkspaceMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: SetDefaultWorkspaceRequest) => {
      return setDefaultWorkspace(input)
    },
    onSuccess: (authMe) => {
      applyAuthoritativeAuthContext(authMe)
      queryClient.setQueryData(buildAuthMeQueryKey(authMe.auth.clerkUserId), authMe)
      void queryClient.invalidateQueries({ queryKey: AUTH_BOOTSTRAP_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: AUTH_TENANT_ACCESS_RESOLVE_QUERY_KEY })
    },
  })
}

export function useCreateBranchMutation() {
  const { getToken } = useAuth()
  return useMutation({
    mutationFn: async (input: { workspaceId: string; name: string; status?: "PENDING" | "ACTIVE" | "INACTIVE" | "SUSPENDED" }) => {
      const token = await getToken()
      return createBranch(
        {
          workspaceId: input.workspaceId,
          name: input.name,
          status: input.status,
        },
        token
      )
    },
  })
}
